/**
 * useStockMovements - Movimentações, equipamentos por veículo e Visão Geral do Estoque
 *
 * 100% via RPCs: `get_stock_movements`, `get_stock_movable_items`,
 * `get_stock_destinations`, `move_stock_items_atomic`,
 * `get_vehicle_installed_equipment` e `get_stock_overview_kpis`.
 *
 * RELEASE: 2026-10-11 v1.8.0 - Equipamentos do veículo sempre relidos; tempo real via canal único; sem polling.
* RELEASE: 2026-10-10 v1.7.0 - STOCK_REFRESH_QUERY_KEYS inclui 'service-order-stock'
 *          (modal Equipamentos utilizados e aba Movimentações da OS).
 * RELEASE: 2026-10-10 v1.6.0 - Itens movimentáveis reconsultam ao abrir/focar, usam
 *          polling visível e Realtime durante o modal; seleção pode ser revalidada antes da gravação.
 * RELEASE: 2026-10-10 v1.5.0 - MovableItem com situações e componentes do kit.
 * RELEASE: 2026-10-09 v1.4.0 - StockMovementRow ganha service_type_name (tipo de serviço da OS).
 * RELEASE: 2026-10-08 v1.3.0 - Filtro individual por produto para fichas de Rastreador, Chip e Kit.
 * RELEASE: 2026-10-08 v1.2.0 - Lista sempre reconsulta ao abrir para evitar histórico desatualizado.
 * RELEASE: 2026-10-08 v1.1.0 - Tipagem detalhada de código, IMEI e ICCID das movimentações.
 * RELEASE: 2026-10-06 v1.0.0 - Criação inicial (Fases 4 e 5 do Estoque).
 */
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useTenantStockRealtime } from '@/hooks/realtime/useTenantStockRealtime';
import type { StockItemKind } from '@/hooks/useStockItems';

import type { StockMovementSourceType } from '@/lib/stock-presentation';

const logger = createLogger('useStockMovements');

/** 'carrier' (Logística) só é gravado pelas remessas; não entra na lista da movimentação manual. */
export type StockDestinationType = 'location' | 'provider' | 'customer' | 'carrier';

export const STOCK_DESTINATION_TYPES: { value: StockDestinationType; label: string }[] = [
  { value: 'location', label: 'Endereço de estoque' },
  { value: 'provider', label: 'Prestador de serviço' },
  { value: 'customer', label: 'Cliente (veículo)' },
];

/** Local atual/anterior devolvido pelo banco. */
export interface StockPlace {
  type: StockDestinationType;
  label: string | null;
  customer_name: string | null;
  vehicle_id: string | null;
  since: string;
}

export interface StockMovementRow {
  id: string;
  batch_id: string;
  created_at: string;
  event_type: 'move' | 'kit_dissolved';
  item_type: StockItemKind;
  item_label: string;
  kit_code: string | null;
  imei: string | null;
  iccid: string | null;
  from_place: StockPlace | null;
  to_place: StockPlace | null;
  notes: string | null;
  created_by_name: string | null;
  /** Origem do pedido (BR-OS-001) e referência do documento vinculado. */
  source_type?: StockMovementSourceType | null;
  source_reference?: string | null;
  /** Tipo de serviço da OS que gerou a movimentação (nulo quando não veio de OS). */
  service_type_name?: string | null;
}

export interface StockMovementsFilters {
  search: string;
  itemType: StockItemKind | 'all';
  /** Produto específico; Rastreador/Chip incluem também movimentações feitas pelo kit vinculado. */
  itemId?: string | null;
  destinationType: StockDestinationType | 'all';
  dateFrom: string | null;
  dateTo: string | null;
  page: number;
  pageSize: number;
}

/** Item selecionável para movimentar. */
export interface MovableItem {
  type: StockItemKind;
  id: string;
  label: string;
  extra?: string | null;
  place: StockPlace | null;
  /** v1.6.0: situação e identificação técnica retornadas por get_stock_movable_items v1.2.0. */
  status?: string | null;
  tracker_imei?: string | null;
  tracker_status?: string | null;
  tracker_model_name?: string | null;
  chip_iccid?: string | null;
  chip_status?: string | null;
  chip_plan_name?: string | null;
  chip_data_package_mb?: number | null;
  chip_supplier_name?: string | null;
  chip_carriers?: string[] | null;
  /** Kit elegível somente com kit, rastreador e chip ativos. */
  eligible?: boolean | null;
  blocked_reason?: string | null;
}

export interface StockOverview {
  totals: { trackers: number; chips: number; kits: number; free_trackers: number; free_chips: number };
  by_place: { location: number; provider: number; customer: number; none: number };
  by_status: Record<StockItemKind, Record<string, number>>;
  by_location: { id: string; name: string; type: string; count: number; kits: number; trackers: number; chips: number }[];
  by_provider: { id: string; name: string; count: number }[];
  stale_count: number;
  stale: { kind: StockItemKind; id: string; label: string; status: string; place: StockPlace | null; since: string }[];
  stale_days: number;
}

export const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';

// Mantém o `this` do cliente ao chamar RPCs fora da tipagem gerada
export const stockRpc = (name: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (n: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>)
    .call(supabase, name, args);

/** Argumentos da RPC de histórico (reaproveitados pela exportação). */
export const movementsRpcArgs = (f: Omit<StockMovementsFilters, 'page' | 'pageSize'>) => ({
  p_search: f.search || null,
  p_item_type: f.itemType === 'all' ? null : f.itemType,
  p_item_id: f.itemId ?? null,
  p_destination_type: f.destinationType === 'all' ? null : f.destinationType,
  p_date_from: f.dateFrom,
  p_date_to: f.dateTo,
  p_timezone: TIMEZONE,
});

/** Histórico paginado. */
export function useStockMovementsList(filters: StockMovementsFilters) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  // Tempo real: canal único de estoque recarrega o histórico
  useTenantStockRealtime([['stock-movements']], !!tenantId);
  return useQuery({
    queryKey: ['stock-movements', tenantId, filters],
    refetchOnWindowFocus: true,
    enabled: !!tenantId,
    // Histórico operacional: ao voltar para a tela, sempre conferir novos registros no servidor.
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await stockRpc('get_stock_movements', {
        ...movementsRpcArgs(filters),
        p_page: filters.page,
        p_page_size: filters.pageSize,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar movimentações', { error: error.message });
        throw new Error(error.message);
      }
      const p = (data || {}) as { total?: number; items?: StockMovementRow[] };
      return { total: p.total ?? 0, items: p.items ?? [] };
    },
  });
}

/** Itens movimentáveis (kits montados e itens fora de kit). */
export function useStockMovableItems(kind: StockItemKind, search: string, enabled: boolean) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['stock-movable', kind, tenantId, search],
    enabled: !!tenantId && enabled,
    // Operação concorrente: nunca considerar a resposta anterior como atual ao reabrir/retomar.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    queryFn: async () => {
      const { data, error } = await stockRpc('get_stock_movable_items', { p_kind: kind, p_search: search || null, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      return (data as MovableItem[]) ?? [];
    },
  });
}

/**
 * Mantém o seletor sincronizado com movimentações feitas por outro usuário ou dispositivo.
 * A assinatura existe somente enquanto o modal está aberto e sempre é removida no cleanup.
 */
export function useStockMovableItemsRealtime(enabled: boolean) {
  // v1.8.0: canal único compartilhado por tenant
  useTenantStockRealtime([['stock-movable']], enabled);
}

/** Reconsulta cada item selecionado pela RPC oficial imediatamente antes da confirmação. */
export function useRevalidateStockMovableItems() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  return async (items: MovableItem[]) => {
    if (!tenantId) throw new Error('Tenant ativo não encontrado');
    const refreshed = await Promise.all(items.map(async (item) => {
      const { data, error } = await stockRpc('get_stock_movable_items', {
        p_kind: item.type,
        p_search: item.label,
        p_tenant_hint: tenantId,
      });
      if (error) throw new Error(error.message);
      return ((data as MovableItem[]) ?? []).find((candidate) => candidate.id === item.id) ?? null;
    }));
    return refreshed;
  };
}

/** Opções de destino. */
export function useStockDestinations(type: StockDestinationType, search: string, enabled: boolean) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['stock-destinations', type, tenantId, search],
    enabled: !!tenantId && enabled,
    queryFn: async () => {
      const { data, error } = await stockRpc('get_stock_destinations', { p_type: type, p_search: search || null, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      return (data as { id: string; label: string; extra: string | null }[]) ?? [];
    },
  });
}

/** Movimentação atômica (um ou vários itens). */
export function useMoveStockItems() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ items, destination, notes }: {
      items: { type: StockItemKind; id: string }[];
      destination: { type: StockDestinationType; id: string };
      notes: string;
    }) => {
      const dest = destination.type === 'customer'
        ? { type: 'customer', vehicle_id: destination.id }
        : { type: destination.type, id: destination.id };
      const { data, error } = await stockRpc('move_stock_items_atomic', {
        p_items: items, p_destination: dest, p_notes: notes || null, p_tenant_hint: tenantId ?? null,
      });
      if (error) throw new Error(error.message);
      return data as { batch_id: string; count: number };
    },
    onSuccess: (d) => {
      STOCK_REFRESH_QUERY_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(d.count === 1 ? '1 item movimentado' : `${d.count} itens movimentados`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/**
 * Raízes de cache atualizadas após qualquer movimentação de estoque
 * (manual ou pela conclusão da OS). Fonte única para não divergir.
 */
export const STOCK_REFRESH_QUERY_KEYS = [
  'stock-movements', 'stock-items', 'stock-movable', 'stock-free', 'stock-overview', 'vehicle-equipment', 'provider-equipment',
  // v1.7.0: itens do modal de conclusão da OS e aba Movimentações da OS
  'service-order-stock',
] as const;

/** Equipamentos instalados e histórico de um veículo. */
export function useVehicleEquipment(vehicleId: string | null) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['vehicle-equipment', tenantId, vehicleId],
    enabled: !!tenantId && !!vehicleId,
    // v1.8.0: sempre relê ao abrir a aba e ao voltar para a janela
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await stockRpc('get_vehicle_installed_equipment', { p_vehicle_id: vehicleId, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      return data as {
        installed: { type: StockItemKind; id: string; label: string; status: string; tracker?: string; chip?: string; line?: string | null; model?: string | null; since: string; service_order_id?: string | null; service_order_number?: string | null }[];
        history: (StockMovementRow & { direction: 'in' | 'out' })[];
      };
    },
  });
}

/**
 * Itens em posse e histórico de um prestador (aba Movimentação do modal de Pessoa).
 * Espelho de `useVehicleEquipment`; fonte: `get_provider_stock_equipment`.
 * RELEASE: 2026-10-14 v1.0.0 - Versão inicial.
 */
export function useProviderEquipment(personId: string | null, enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['provider-equipment', tenantId, personId],
    enabled: !!tenantId && !!personId && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await stockRpc('get_provider_stock_equipment', { p_person_id: personId, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      return data as {
        holding: { kits: number; trackers: number; chips: number };
        history: (StockMovementRow & { direction: 'in' | 'out' })[];
      };
    },
  });
}

/** Indicadores da Visão Geral. */
export function useStockOverview(staleDays: number) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['stock-overview', tenantId, staleDays],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await stockRpc('get_stock_overview_kpis', { p_stale_days: staleDays, p_tenant_hint: tenantId ?? null });
      if (error) {
        logger.error('Erro ao carregar visão geral do estoque', { error: error.message });
        throw new Error(error.message);
      }
      return data as StockOverview;
    },
  });
}
